import {
  Fragment,
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import clsx from "clsx";
import { clamp, isNil } from "lodash";
import { Portal } from "react-portal";
import { useOnClickOutside } from "usehooks-ts";

import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { efficiencyKpiManager } from "@features/mmm/utils/efficiency-kpi-manager";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { type BudgetMixResponse } from "@north-beam/nb-common";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useClientSettingsColorMap } from "@pages/mmm/hooks/use-client-settings-color-map";
import { renderChevronAnimation } from "@shared/selects/utils";

import { AdoptedChip } from "../../shared/kpi-select";
import { type Kpi } from "../client-setting/types";

type Props = {
  selectedBudgetMix: BudgetMixResponse;
  setSelectedBudgetMixId: (budgetMixId: null | number | undefined) => void;
  selectedKpi: Kpi;
  formatCurrencyApproximate: (value: number) => string;
  formatValueApproximate: (value: number) => string;
  kpiTypeDisplayName: string;
};

export const BudgetMixSelect = ({
  selectedBudgetMix,
  setSelectedBudgetMixId,
  selectedKpi,
  formatCurrencyApproximate,
  formatValueApproximate,
  kpiTypeDisplayName,
}: Props) => {
  const clientSettingsColorMap = useClientSettingsColorMap();
  const clientSettingColor =
    clientSettingsColorMap[selectedBudgetMix.client_setting_id || ""];
  const [isOpen, setIsOpen] = useState(false);
  const [currentHoveredIndex, setCurrentHoveredIndex] = useState<
    number | null
  >();
  const [budgetMixes] = useBudgetMixes();
  const { clientSettings } = useClientSettings();
  const { getKpi, getSpendConstraints } = useClientSettingData();
  const { user } = useUser();

  const budgetMixesToShow = useMemo(() => {
    const adoptedBudgetMix = budgetMixes.find(
      (budgetMix) => budgetMix.is_adopted,
    );
    const mixes = budgetMixes.filter((budgetMix) => !budgetMix.is_adopted);
    if (adoptedBudgetMix) {
      mixes.unshift(adoptedBudgetMix);
    }
    return mixes;
  }, [budgetMixes]);

  const popoverPositionRef = useRef<HTMLDivElement>(null);
  const popoverContainerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(popoverContainerRef, () => setIsOpen(false));

  const [xPosition, setXPosition] = useState(0);
  const [yPosition, setYPosition] = useState(0);
  useEffect(() => {
    if (popoverPositionRef.current) {
      const rect = popoverPositionRef.current.getBoundingClientRect();
      setXPosition(rect.left);
      setYPosition(rect.bottom);
    }

    if (!isOpen) {
      setCurrentHoveredIndex(null);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    ({ key }) => {
      if (key === "Escape") {
        setIsOpen(false);
      } else if (key === "ArrowDown") {
        setCurrentHoveredIndex(
          clamp(
            currentHoveredIndex === null ? 0 : Number(currentHoveredIndex) + 1,
            0,
            budgetMixesToShow.length - 1,
          ),
        );
        currentHoveredIndex &&
          document
            .getElementById(
              `budget-mix-${budgetMixesToShow[currentHoveredIndex].id}`,
            )
            ?.scrollIntoView({ block: "center" });
      } else if (key === "ArrowUp") {
        setCurrentHoveredIndex(
          clamp(
            Number(currentHoveredIndex) - 1,
            0,
            budgetMixesToShow.length - 1,
          ),
        );
        currentHoveredIndex &&
          document
            .getElementById(
              `budget-mix-${budgetMixesToShow[currentHoveredIndex].id}`,
            )
            ?.scrollIntoView({ block: "center" });
      } else if (key === "Enter") {
        if (!isNil(currentHoveredIndex)) {
          setSelectedBudgetMixId(budgetMixesToShow[currentHoveredIndex].id);
          setIsOpen(false);
        }
      }
    },
    [currentHoveredIndex, budgetMixesToShow, setSelectedBudgetMixId],
  );

  return (
    <div ref={popoverPositionRef} className="flex items-center gap-2 relative">
      {clientSettingColor ? (
        <div
          className="h-4 w-4 rounded-sm inline-block"
          style={{ backgroundColor: clientSettingColor }}
        ></div>
      ) : (
        <i className="fa-regular fa-file-csv text-gray h-4 w-4 inline-block" />
      )}
      {selectedBudgetMix.name}
      <Popover>
        <PopoverButton
          className="w-8 h-8 rounded-full border flex-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
        >
          {renderChevronAnimation(isOpen, false, null, "text-sm")}
        </PopoverButton>
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        <Portal node={document.getElementById("root")!}>
          <PopoverPanel static>
            {isOpen && (
              <div
                ref={popoverContainerRef}
                className="absolute z-[2500] mt-2 elevation-1 rounded-md bg-v2-primary w-[500px] h-96 overflow-y-auto"
                style={{ left: `${xPosition}px`, top: `${yPosition}px` }}
              >
                {budgetMixesToShow.map((budgetMix, index) => {
                  const summaryData =
                    budgetMix.data.aggregated_data.summary_data;
                  const nameKey = selectedKpi?.kpi_name;
                  const clientSetting = clientSettings.find(
                    (setting) => setting.id === budgetMix.client_setting_id,
                  );
                  const budgetMixClientSettingColor =
                    clientSettingsColorMap[budgetMix.client_setting_id || ""];
                  const predictedValues = nameKey
                    ? summaryData.kpi_data[nameKey]?.values
                    : null;

                  const {
                    efficiencyKpiFormatter,
                    efficiencyKpiCalculator,
                    efficiencyKpiType,
                  } = efficiencyKpiManager(selectedKpi, user);
                  let derivedMetricLow = 0;
                  let derivedMetricHigh = 0;

                  if (predictedValues) {
                    derivedMetricLow = efficiencyKpiCalculator(
                      summaryData.spend,
                      predictedValues.low,
                    );
                    derivedMetricHigh = efficiencyKpiCalculator(
                      summaryData.spend,
                      predictedValues.high,
                    );
                  }

                  return (
                    <Fragment key={budgetMix.id}>
                      <div
                        id={`budget-mix-${budgetMix.id}`}
                        className={clsx("space-y-1.5 p-4 cursor-pointer", {
                          "bg-cosmic-4": currentHoveredIndex === index,
                        })}
                        onClick={() => {
                          setSelectedBudgetMixId(budgetMix.id);
                          setIsOpen(false);
                          setCurrentHoveredIndex(null);
                        }}
                        onMouseEnter={() => setCurrentHoveredIndex(index)}
                        onMouseLeave={() => setCurrentHoveredIndex(null)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold truncate">
                            {budgetMix.name}
                          </div>
                          {budgetMix.is_adopted && <AdoptedChip />}
                        </div>
                        <div className="flex items-center gap-7">
                          {predictedValues && (
                            <div>
                              <div className="text-3xs font-semibold">
                                PREDICTED {kpiTypeDisplayName.toUpperCase()}{" "}
                                RANGE
                              </div>
                              <div className="text-sm text-cosmic">
                                {`${formatValueApproximate(
                                  predictedValues.low,
                                )} - ${formatValueApproximate(
                                  predictedValues.high,
                                )}`}
                              </div>
                            </div>
                          )}
                          <div className="h-4 border-l"></div>
                          <div>
                            <div className="text-3xs font-semibold">SPEND</div>
                            <div className="text-sm text-cosmic">
                              {formatCurrencyApproximate(summaryData.spend)}
                            </div>
                          </div>
                          <div className="h-4 border-l"></div>
                          {predictedValues && (
                            <div>
                              <div className="text-3xs font-semibold">
                                PREDICTED i{efficiencyKpiType}
                              </div>
                              <div className="text-sm text-cosmic">
                                {`${efficiencyKpiFormatter(
                                  derivedMetricLow,
                                )} - ${efficiencyKpiFormatter(
                                  derivedMetricHigh,
                                )}`}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-2xs whitespace-nowrap">
                          <div className="flex items-center gap-2 bg-cosmic-4 rounded p-2.5">
                            {clientSetting ? (
                              <>
                                <div className="flex items-center gap-1">
                                  <div
                                    className="h-3 w-3 min-w-[12px] rounded-sm"
                                    style={{
                                      backgroundColor:
                                        budgetMixClientSettingColor,
                                    }}
                                  ></div>
                                  <Tooltip
                                    content={clientSetting.name}
                                    noInfoCircle
                                    className="truncate max-w-[65px]"
                                    showTooltip={clientSetting.name.length > 10}
                                  >
                                    <span className="truncate max-w-[65px]">
                                      {clientSetting.name}
                                    </span>
                                  </Tooltip>
                                </div>
                                <div className="h-3 border-l"></div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray+2">Budget</span>
                                  <span>
                                    {formatCurrencyApproximate(
                                      clientSetting.budget_low,
                                    )}{" "}
                                    -{" "}
                                    {formatCurrencyApproximate(
                                      clientSetting.budget_high,
                                    )}
                                  </span>
                                </div>
                                <div className="h-3 border-l"></div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray+2 uppercase">
                                    Kpi
                                  </span>
                                  <Tooltip
                                    content={
                                      getKpi(clientSetting)?.friendly_name
                                    }
                                    noInfoCircle
                                    className="truncate max-w-[65px]"
                                    showTooltip={
                                      (getKpi(clientSetting)?.friendly_name
                                        .length ?? 0) > 10
                                    }
                                  >
                                    <span className="truncate max-w-[65px]">
                                      {getKpi(clientSetting)?.friendly_name}
                                    </span>
                                  </Tooltip>
                                </div>
                                <div className="h-3 border-l"></div>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray+2">
                                    Spend requirements
                                  </span>
                                  <span>
                                    {getSpendConstraints(clientSetting).length}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <i className="fa-regular fa-file-csv text-gray h-3 w-3 inline-block" />{" "}
                                Custom budget
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {index !== budgetMixes.length - 1 && (
                        <div className="border-b border-v2-secondary mx-4"></div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}
          </PopoverPanel>
        </Portal>
      </Popover>
    </div>
  );
};
